import {
  WizardHeader,
  WizardLink,
} from '@monorepo/shared/componentsV2/fullPageWizard/WizardHeader';
import { useLinkOnSameOrg } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import { useIsSuperAdmin } from '@monorepo/shared/hooks/permissions/useIsSuperAdmin';
import { NonEmptyArray } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ResourceTypeEditMode } from '../../types/routerTypes';

export enum WizardStep {
  DETAILS = 'Basic Info',
  PROPERTIES = 'Fields',
  RESOURCES = 'Add Resources',
  ANALYTICS_SYNC = 'Analytics',
}

interface EditResourceTypeHeaderProps {
  canSubmit?: boolean;
  isSubmitting?: boolean;
  onSubmit?: () => unknown;
  wizardStep: WizardStep;
}

export function EditResourceTypeHeader({
  canSubmit,
  isSubmitting = false,
  onSubmit,
  wizardStep,
}: EditResourceTypeHeaderProps) {
  const isSuperAdmin = useIsSuperAdmin();

  const { mode = ResourceTypeEditMode.NEW, resourceTypeId } = useParams();
  const isEditMode = mode === ResourceTypeEditMode.EDIT;
  const resourceTypeExists = !!resourceTypeId;

  const navigate = useNavigate();

  const baseLink = useLinkOnSameOrg('/data-setup/resource-types');

  const getLink = useCallback(
    (relativePath: string) =>
      `${baseLink}/${resourceTypeId}/${
        isEditMode ? 'edit' : 'new'
      }${relativePath}`,
    [baseLink, resourceTypeId, isEditMode],
  );

  const handleClose = useCallback(() => {
    navigate(baseLink);
  }, [navigate, baseLink]);

  const fieldsAreNotReady =
    !isEditMode &&
    (wizardStep === WizardStep.DETAILS ||
      (wizardStep === WizardStep.PROPERTIES && !canSubmit));

  const breadcrumbsLinks = useMemo<NonEmptyArray<WizardLink>>(
    () => [
      {
        completed: resourceTypeExists,
        label: WizardStep.DETAILS,
        to: `${baseLink}${resourceTypeExists ? `/${resourceTypeId}` : ''}/${
          isEditMode ? 'edit' : 'new'
        }${resourceTypeExists ? `/basic-info` : ''}`,
      },
      {
        completed:
          wizardStep !== WizardStep.DETAILS &&
          wizardStep !== WizardStep.PROPERTIES,
        disabled: !resourceTypeExists,
        label: WizardStep.PROPERTIES,
        to: getLink('/properties'),
      },
      {
        disabled: !resourceTypeExists || fieldsAreNotReady,
        label: WizardStep.RESOURCES,
        to: getLink('/add-resources'),
      },
      ...(isSuperAdmin && isEditMode
        ? [
            {
              disabled: false,
              label: WizardStep.ANALYTICS_SYNC,
              to: getLink('/analytics'),
            },
          ]
        : []),
    ],
    [
      resourceTypeExists,
      baseLink,
      resourceTypeId,
      isEditMode,
      wizardStep,
      getLink,
      fieldsAreNotReady,
      isSuperAdmin,
    ],
  );

  return (
    <WizardHeader
      canSubmit={canSubmit}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      links={breadcrumbsLinks}
      onClose={handleClose}
      onSubmit={onSubmit}
      title={isEditMode ? 'Edit Resource Type' : 'New Resource Type'}
      wizardStep={wizardStep}
    />
  );
}
